/**
 * FinanceiroV2 — Portal Financeiro Billing V2
 * Fase 5.10 — Usa APIs reais do Billing V2 (nunca Company.dueDate / legado)
 *
 * Segurança:
 * - Isolamento por req.user.companyId (server-side)
 * - Não expõe payload do gateway
 * - Não exibe linha digitável de boleto a não ser quando necessário
 * - Masking de referências externas
 * - Sem dangerouslySetInnerHTML
 */
import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import "./styles.css";

/* ─── Formatters ───────────────────────────────────────────────── */
const fmtCurrency = (cents, currency = "BRL") => {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency
    }).format((cents || 0) / 100);
  } catch {
    return `R$ ${((cents || 0) / 100).toFixed(2)}`;
  }
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR").format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const maskRef = (ref) => {
  if (!ref || ref.length <= 8) return ref || "—";
  return `${ref.slice(0, 4)}...${ref.slice(-4)}`;
};

/* ─── Status labels ────────────────────────────────────────────── */
const subscriptionStatusLabel = {
  active: { label: "Ativa", cls: "fin__badge fin__badge--green" },
  pending: { label: "Pagamento pendente", cls: "fin__badge fin__badge--yellow" },
  past_due: { label: "Em atraso", cls: "fin__badge fin__badge--red" },
  grace_period: { label: "Em carência", cls: "fin__badge fin__badge--orange" },
  suspended: { label: "Suspensa", cls: "fin__badge fin__badge--red" },
  cancel_scheduled: { label: "Cancelamento agendado", cls: "fin__badge fin__badge--gray" },
  cancelled: { label: "Cancelada", cls: "fin__badge fin__badge--gray" },
  manual_review: { label: "Em análise", cls: "fin__badge fin__badge--blue" },
  inactive: { label: "Inativa", cls: "fin__badge fin__badge--gray" }
};

const invoiceStatusLabel = {
  open: "Em aberto",
  pending: "Pendente",
  paid: "Pago",
  partially_paid: "Parcialmente pago",
  overdue: "Vencido",
  cancelled: "Cancelado",
  void: "Anulado"
};

const paymentMethodLabel = {
  PIX: "PIX",
  BOLETO: "Boleto",
  CREDIT_CARD: "Cartão de crédito"
};

/* ─── Tabs ─────────────────────────────────────────────────────── */
const TABS = [
  { id: "overview", label: "Visão Geral" },
  { id: "invoices", label: "Faturas" },
  { id: "payments", label: "Pagamentos" },
  { id: "billing-profile", label: "Dados de Cobrança" }
];

/* ─── StatusBadge ──────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const cfg = subscriptionStatusLabel[status] || { label: status || "Desconhecido", cls: "fin__badge fin__badge--gray" };
  return <span className={cfg.cls}>{cfg.label}</span>;
};

/* ─── RegularizationBanner ─────────────────────────────────────── */
const RegularizationBanner = ({ status, onPay }) => {
  const needsAction = ["past_due", "grace_period", "suspended"].includes(status);
  if (!needsAction) return null;

  const messages = {
    past_due: "Sua assinatura está em atraso. Regularize para manter o acesso.",
    grace_period: "Sua assinatura está no período de carência. Regularize para evitar suspensão.",
    suspended: "Sua assinatura está suspensa. Realize o pagamento para reativar."
  };

  return (
    <div className="fin__banner fin__banner--warning" role="alert">
      <div className="fin__bannerContent">
        <strong>Atenção:</strong> {messages[status]}
        <div className="fin__bannerActions">
          <button
            id="fin-regularization-pay-btn"
            className="fin__btn fin__btn--primary fin__btn--sm"
            onClick={onPay}
          >
            Regularizar agora
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── OverviewTab ──────────────────────────────────────────────── */
const OverviewTab = ({ overview, onGoToInvoices }) => {
  if (!overview) return <div className="fin__loading">Carregando visão geral...</div>;

  const sub = overview.subscription;
  const inv = overview.latestInvoice;
  const pay = overview.latestPayment;

  return (
    <div className="fin__section">
      <div className="fin__overviewGrid">
        <div className="fin__card">
          <div className="fin__cardLabel">Plano atual</div>
          <div className="fin__cardValue">{sub?.planName || "—"}</div>
          {sub && <StatusBadge status={sub.status} />}
        </div>

        <div className="fin__card">
          <div className="fin__cardLabel">Valor</div>
          <div className="fin__cardValue fin__cardValue--large">
            {sub ? fmtCurrency(sub.amountCents, sub.currency) : "—"}
          </div>
          {sub?.billingCycle && (
            <div className="fin__cardSub">por {sub.billingCycle === "monthly" ? "mês" : sub.billingCycle}</div>
          )}
        </div>

        <div className="fin__card">
          <div className="fin__cardLabel">Período atual</div>
          <div className="fin__cardValue">
            {sub?.currentPeriodStart ? fmtDate(sub.currentPeriodStart) : "—"}
            {sub?.currentPeriodEnd ? ` → ${fmtDate(sub.currentPeriodEnd)}` : ""}
          </div>
        </div>

        <div className="fin__card">
          <div className="fin__cardLabel">Modo de cobrança</div>
          <div className="fin__cardValue">{overview.billingMode === "AUTOMATIC" ? "Automático" : "Manual"}</div>
        </div>
      </div>

      {inv && (
        <div className="fin__subsection">
          <h3 className="fin__subsectionTitle">Última fatura</h3>
          <div className="fin__row">
            <span className="fin__rowLabel">Situação</span>
            <span className="fin__rowValue">
              <span className={`fin__badge ${inv.status === "paid" ? "fin__badge--green" : "fin__badge--yellow"}`}>
                {invoiceStatusLabel[inv.status] || inv.status}
              </span>
            </span>
          </div>
          <div className="fin__row">
            <span className="fin__rowLabel">Valor</span>
            <span className="fin__rowValue">{fmtCurrency(inv.amountCents, inv.currency)}</span>
          </div>
          <div className="fin__row">
            <span className="fin__rowLabel">Vencimento</span>
            <span className="fin__rowValue">{fmtDate(inv.dueDateV2)}</span>
          </div>
          {inv.status !== "paid" && (
            <div className="fin__rowActions">
              <button
                id="fin-overview-pay-invoice-btn"
                className="fin__btn fin__btn--primary fin__btn--sm"
                onClick={onGoToInvoices}
              >
                Ver e pagar fatura
              </button>
            </div>
          )}
        </div>
      )}

      {pay && (
        <div className="fin__subsection">
          <h3 className="fin__subsectionTitle">Último pagamento</h3>
          <div className="fin__row">
            <span className="fin__rowLabel">Data</span>
            <span className="fin__rowValue">{fmtDate(pay.paidAt)}</span>
          </div>
          <div className="fin__row">
            <span className="fin__rowLabel">Valor</span>
            <span className="fin__rowValue">{fmtCurrency(pay.amountCents, pay.currency)}</span>
          </div>
          <div className="fin__row">
            <span className="fin__rowLabel">Método</span>
            <span className="fin__rowValue">{paymentMethodLabel[pay.paymentMethod] || pay.paymentMethod}</span>
          </div>
        </div>
      )}

      {/* Ações disponíveis */}
      {Array.isArray(overview.availableActions) && overview.availableActions.length > 0 && (
        <div className="fin__subsection">
          <h3 className="fin__subsectionTitle">Ações disponíveis</h3>
          <div className="fin__actions">
            {overview.availableActions.includes("pay_invoice") && (
              <button
                id="fin-overview-action-pay"
                className="fin__btn fin__btn--secondary"
                onClick={onGoToInvoices}
              >
                Pagar fatura pendente
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── InvoicesTab ──────────────────────────────────────────────── */
const InvoicesTab = ({ invoices, loading }) => {
  if (loading) return <div className="fin__loading">Carregando faturas...</div>;
  if (!invoices || invoices.length === 0) {
    return <div className="fin__empty">Nenhuma fatura encontrada.</div>;
  }

  return (
    <div className="fin__section">
      <div className="fin__tableWrapper">
        <table className="fin__table" aria-label="Faturas">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tipo</th>
              <th>Vencimento</th>
              <th>Valor</th>
              <th>Situação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.publicId || inv.id}>
                <td className="fin__mono">{inv.publicId ? inv.publicId.slice(-8) : "—"}</td>
                <td>{inv.invoiceType === "initial_subscription" ? "Assinatura inicial" : "Renovação"}</td>
                <td>{fmtDate(inv.dueDate)}</td>
                <td className="fin__amount">{fmtCurrency(inv.amountCents, inv.currency)}</td>
                <td>
                  <span className={`fin__badge ${inv.status === "paid" ? "fin__badge--green" : inv.status === "open" ? "fin__badge--yellow" : "fin__badge--gray"}`}>
                    {invoiceStatusLabel[inv.status] || inv.status}
                  </span>
                </td>
                <td>
                  {inv.status !== "paid" && (
                    <button
                      id={`fin-invoice-pay-${inv.publicId}`}
                      className="fin__btn fin__btn--sm fin__btn--primary"
                    >
                      Pagar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── PaymentsTab ──────────────────────────────────────────────── */
const PaymentsTab = ({ payments, loading }) => {
  if (loading) return <div className="fin__loading">Carregando pagamentos...</div>;
  if (!payments || payments.length === 0) {
    return <div className="fin__empty">Nenhum pagamento registrado.</div>;
  }

  return (
    <div className="fin__section">
      <div className="fin__tableWrapper">
        <table className="fin__table" aria-label="Pagamentos">
          <thead>
            <tr>
              <th>Data</th>
              <th>Valor</th>
              <th>Método</th>
              <th>Situação</th>
              <th>Referência</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.publicId || p.id}>
                <td>{fmtDate(p.paidAt)}</td>
                <td className="fin__amount">{fmtCurrency(p.amountCents, p.currency)}</td>
                <td>{paymentMethodLabel[p.paymentMethod] || p.paymentMethod}</td>
                <td>
                  <span className={`fin__badge ${p.status === "confirmed" ? "fin__badge--green" : "fin__badge--gray"}`}>
                    {p.status === "confirmed" ? "Confirmado" : p.status}
                  </span>
                </td>
                {/* Referência mascarada — não expõe payload do gateway */}
                <td className="fin__mono">{maskRef(p.publicId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── BillingProfileTab ────────────────────────────────────────── */
const BillingProfileTab = ({ profile, loading }) => {
  if (loading) return <div className="fin__loading">Carregando perfil de cobrança...</div>;
  if (!profile) return <div className="fin__empty">Perfil de cobrança não encontrado.</div>;

  return (
    <div className="fin__section">
      <div className="fin__subsection">
        <h3 className="fin__subsectionTitle">Dados de cobrança</h3>
        <div className="fin__row">
          <span className="fin__rowLabel">Modo</span>
          <span className="fin__rowValue">{profile.billingMode === "AUTOMATIC" ? "Automático (Billing V2)" : "Manual (legado)"}</span>
        </div>
        <div className="fin__row">
          <span className="fin__rowLabel">Cobrança ativa</span>
          <span className="fin__rowValue">
            <span className={`fin__badge ${profile.billingEnabled ? "fin__badge--green" : "fin__badge--gray"}`}>
              {profile.billingEnabled ? "Sim" : "Não"}
            </span>
          </span>
        </div>
        <div className="fin__row">
          <span className="fin__rowLabel">Moeda</span>
          <span className="fin__rowValue">{profile.currency || "BRL"}</span>
        </div>
        <div className="fin__row">
          <span className="fin__rowLabel">Fuso horário</span>
          <span className="fin__rowValue">{profile.timezone || "—"}</span>
        </div>
        <div className="fin__row">
          <span className="fin__rowLabel">Renovação automática</span>
          <span className="fin__rowValue">{profile.automaticRenewalEnabled ? "Ativa" : "Inativa"}</span>
        </div>
        <div className="fin__row">
          <span className="fin__rowLabel">Trial utilizado</span>
          <span className="fin__rowValue">{profile.hasUsedTrial ? "Sim" : "Não"}</span>
        </div>
      </div>
      <p className="fin__note">
        Para alterar dados de cobrança ou fiscais, entre em contato com o suporte.
      </p>
    </div>
  );
};

/* ─── FinanceiroV2 (main) ──────────────────────────────────────── */
const FinanceiroV2 = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      const { data } = await api.get("/billing/client/overview");
      setOverview(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const { data } = await api.get("/billing/client/invoices");
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const { data } = await api.get("/billing/client/payments");
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const { data } = await api.get("/billing/client/billing-profile");
      setProfile(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Load per tab
  useEffect(() => {
    if (activeTab === "invoices" && invoices.length === 0) fetchInvoices();
    if (activeTab === "payments" && payments.length === 0) fetchPayments();
    if (activeTab === "billing-profile" && !profile) fetchProfile();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const subStatus = overview?.subscription?.status;
  const needsRegularization = ["past_due", "grace_period", "suspended"].includes(subStatus);

  const handleGoToInvoices = () => {
    setActiveTab("invoices");
    if (invoices.length === 0) fetchInvoices();
  };

  return (
    <div className="fin__root">
      <header className="fin__header">
        <h1 className="fin__title">Portal Financeiro</h1>
        <p className="fin__subtitle">Gerencie sua assinatura, faturas e pagamentos</p>
      </header>

      {/* Regularization banner */}
      {needsRegularization && (
        <RegularizationBanner
          status={subStatus}
          onPay={handleGoToInvoices}
        />
      )}

      {/* Tabs */}
      <nav className="fin__tabs" role="tablist" aria-label="Seções do portal financeiro">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            id={`fin-tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`fin__tab${activeTab === tab.id ? " fin__tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="fin__content" role="tabpanel" aria-labelledby={`fin-tab-${activeTab}`}>
        {activeTab === "overview" && (
          <OverviewTab
            overview={loadingOverview ? null : overview}
            onGoToInvoices={handleGoToInvoices}
          />
        )}
        {activeTab === "invoices" && (
          <InvoicesTab invoices={invoices} loading={loadingInvoices} />
        )}
        {activeTab === "payments" && (
          <PaymentsTab payments={payments} loading={loadingPayments} />
        )}
        {activeTab === "billing-profile" && (
          <BillingProfileTab profile={profile} loading={loadingProfile} />
        )}
      </main>
    </div>
  );
};

export default FinanceiroV2;
